import { ApolloClient, InMemoryCache, createHttpLink, from, split } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { getIdToken } from './firebase';
import { store } from './store';
import { setErrorMessage } from './store/messageSlice';
import { getErrorMessage } from './utils/helper';

const httpUri = process.env.REACT_APP_HASURA_URL || 'https://backend.services-test2.pempem.org/graphql';
const wsUri = httpUri.replace('https', 'wss').replace('http', 'ws');

// HTTP link for queries and mutations
const httpLink = createHttpLink({
  uri: httpUri,
});

// Auth link to inject token
const authLink = setContext(async (_, { headers }) => {
  const token = getIdToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'x-hasura-role': 'user',
      "x-app-name": "Supplier-web"
    },
  };
});

// Error link to catch GraphQL errors globally

const errorLink = new ErrorLink(({ error }) => {
  console.log('error link', error)
  if (!error) return;

  // GraphQL errors
  if ("errors" in error && Array.isArray(error.errors)) {
    error.errors.forEach((err) => {
      console.error("[GraphQL error]", err.message);
      store.dispatch(setErrorMessage(getErrorMessage(err.message)));
    });
  }

  // Network / other errors
  if (error instanceof Error) {
    console.error("[Network error]", error.message);
    store.dispatch(setErrorMessage(getErrorMessage(error.message)));
  }
});

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUri,
    connectionParams: async () => {
      const token = getIdToken();
      return {
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          errorLink,
          'x-hasura-role': 'user',
        },
      };
    },
  })
);

// Split links based on operation type
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  from([errorLink, authLink, httpLink])
);

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

interface ApolloProviderWrapperProps {
  children: React.ReactNode;
}

export const ApolloProviderWrapper: React.FC<ApolloProviderWrapperProps> = ({ children }) => {
  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
